<!--
╔════════════════════════════════════════════════════════════════════╗
║  SectionCenterImageHtml.vue - Centro Immagini con HTML            ║
╚════════════════════════════════════════════════════════════════════╝

DESCRIZIONE:
Componente per la visualizzazione di contenuti "Opere d'Arte" con layout
centrato. Gestisce testo HTML sanitizzato, immagini singole/multiple
(carosello), PDF e lightbox interattivo.

FEATURES:
  ✓ Supporto per immagini singole o carosello dinamico
  ✓ Visualizzatore PDF integrato (PdfBlock -> PdfViewer, caricato pigro)
  ✓ Lightbox modal per zoom immagini
  ✓ HTML content sanitizzato
  ✓ Layout centrato e responsive
  ✓ Animazioni smooth (fade-in, scale)
  ✓ Debug logging per diagnostica dati

LAYOUT:
  - Intestazione con eyebrow e titolo centrati
  - Corpo con testo HTML e media (PDF/Immagini/Carosello)
  - Lightbox overlay per visualizzazione fullscreen

DIPENDENZE:
  - ImageCarousel.vue (per multiple immagini)
  - PdfBlock.vue (involucro pigro di PdfViewer.vue)
  - sanitizeHtmlRegex (utility)
-->

<template>
  <section class="section-center-image-html">
    <div class="container">
      <div class="content">
        
        <div class="header">
          <div class="eyebrow">{{ content.eyebrow }}</div>
          <h2 class="title">{{ content.title }}</h2>
        </div>

        <div class="body">
          
          <template v-if="!isTextExpanded">
            <div 
              ref="textTruncatedRef"
              class="html-content html-content-truncated" 
              v-html="sanitizeHtml(content.text)"
            ></div>
            <div v-if="isTextTruncated" class="expand-text-container">
              <button
                @click="isTextExpanded = true"
                class="expand-text-button"
              >
                Mostra altro
              </button>
            </div>
          </template>

          <template v-else>
            <div 
              class="html-content html-content-expanded" 
              v-html="sanitizeHtml(content.text)"
            ></div>
            <div class="expand-text-container">
              <button
                @click="isTextExpanded = false"
                class="expand-text-button"
              >
                Mostra meno
              </button>
            </div>
          </template>

          <div class="pdf-container" v-if="content.pdf">
            <PdfBlock 
              :src="content.pdf" 
              :title="content.title"
            />
          </div>

          <div class="image-container" v-else-if="isSingleImage">
            <img 
              :src="content.image[0]" 
              :alt="content.title"
              class="content-image"
              @click="openLightbox"
            >
          </div>

          <div class="carousel-container-wrapper" v-else-if="isMultipleImages">
            <ImageCarousel 
              :images="content.image" 
              :title="content.title"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="isSingleImage && lightboxOpen" class="lightbox" @click="closeLightbox">
      <div class="lightbox-content">
        <img 
          :src="content.image[0]" 
          :alt="content.title"
          class="lightbox-image"
          @click.stop
        >
        <button class="lightbox-close" @click="closeLightbox">&times;</button>
      </div>
    </div>
  </section>
</template>

<script>
import ImageCarousel from './ImageCarousel.vue'
import PdfBlock from '@/components/sections/shared/PdfBlock.vue'
import { sanitizeHtmlRegex } from '@/utils/sanitizeHtml'

export default {
  name: 'SectionCenterImageHtml',
  components: {
    ImageCarousel,
    PdfBlock
  },

  // ──────────────────────────────────────────────────────────────
  // PROPS DEFINITION
  // ──────────────────────────────────────────────────────────────
  props: {
    /**
     * Contenuto della sezione
     * @type {Object}
     * @required
     * @property {string} eyebrow - Testo eyebrow (sopra titolo)
     * @property {string} title - Titolo principale
     * @property {string} text - Testo HTML da sanitizzare
     * @property {string|Array} image - URL/Array di URL immagini
     * @property {string} pdf - URL PDF (prioritario su immagini)
     */
    content: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      /**
       * Stato lightbox
       * @type {boolean}
       */
      lightboxOpen: false,
      /**
       * Stato testo espanso (mobile)
       * @type {boolean}
       */
      isTextExpanded: false,
      /**
       * Flag se il testo è troncato
       * @type {boolean}
       */
      isTextTruncated: false
    }
  },

  // ──────────────────────────────────────────────────────────────
  // COMPUTED PROPERTIES
  // ──────────────────────────────────────────────────────────────
  computed: {
    /**
     * Verifica se content.image è un array con 1 elemento
     * @returns {boolean} true se è singola immagine
     */
    isSingleImage() {
      return Array.isArray(this.content.image) && this.content.image.length === 1
    },

    /**
     * Verifica se content.image è un array con più elementi
     * @returns {boolean} true se sono multiple immagini
     */
    isMultipleImages() {
      return Array.isArray(this.content.image) && this.content.image.length > 1
    }
  },

  // ──────────────────────────────────────────────────────────────
  // WATCHERS
  // ──────────────────────────────────────────────────────────────
  watch: {
    'content.text'() {
      this.isTextExpanded = false
      this.checkTextTruncation()
    }
  },

  // ──────────────────────────────────────────────────────────────
  // METHODS
  // ──────────────────────────────────────────────────────────────
  methods: {
    /**
     * Sanitizza HTML per evitare XSS
     * @param {string} html - HTML da sanitizzare
     * @returns {string} HTML sanitizzato
     */
    sanitizeHtml(html) {
      return sanitizeHtmlRegex(html || '')
    },

    /**
     * Controlla se il testo è troncato verificando se l'elemento ha altezza limitata da webkit-line-clamp
     */
    checkTextTruncation() {
      this.$nextTick(() => {
        const elem = this.$refs.textTruncatedRef
        if (elem && !this.isTextExpanded) {
          // Se il testo è in overflow, cioè scrollHeight > clientHeight, allora è troncato
          this.isTextTruncated = elem.scrollHeight > elem.clientHeight
        } else {
          this.isTextTruncated = false
        }
      })
    },

    /**
     * Apre lightbox e disabilita scroll
     */
    openLightbox() {
      this.lightboxOpen = true
      document.body.style.overflow = 'hidden'
    },

    /**
     * Chiude lightbox e ripristina scroll
     */
    closeLightbox() {
      this.lightboxOpen = false
      document.body.style.overflow = ''
    }
  },

  // ──────────────────────────────────────────────────────────────
  // LIFECYCLE HOOKS
  // ──────────────────────────────────────────────────────────────
  mounted() {
    this.checkTextTruncation()
  },

  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

/* ──────────────────────────────────────────────────────────────
   SECTION BASE STYLES - Layout centrato
   ────────────────────────────────────────────────────────────── */
.section-center-image-html {
  padding: $section-padding-y $container-padding-x $space-4 $container-padding-x;

  .container {
    max-width: $container-max-width;
    margin: 0 auto;
  }

  .content {
    display: flex;
    flex-direction: column;
    @include flex-gap(clamp(2rem, 4vw, 3rem), column);
    align-items: center;
    text-align: left;

    /* ──────────────────────────────────────────────────────────────
       HEADER - Eyebrow e Titolo centrati
       ────────────────────────────────────────────────────────────── */
    .header {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      .eyebrow {
        position: relative;
        display: inline-block;
        font-size: $font-eyebrow;
        font-weight: 600;
        text-transform: uppercase;
        color: $clr-tertiary;
        margin-bottom: $space-2;
        cursor: default;
        white-space: nowrap; 
        transition: all 0.3s ease;

        &::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 120%;
          height: 12px;
          background-color: rgba($clr-tertiary, 0.7);
          transition: all 0.5s ease;
        }

        &:hover {
          color: $clr-tertiary;
          
          &::before {
            background: rgba($clr-tertiary, 0.8);
            width: 140%;
            transform: translateX(-50%) scaleY(1.2);
          }
        }
      }

      .title {
        font-size: $font-title;
        font-weight: 300;
        margin: 0;
        color: $text-color;
        margin-bottom: $space-1;
      }
    }

    /* ──────────────────────────────────────────────────────────────
       BODY - Testo HTML e Media container
       ────────────────────────────────────────────────────────────── */
    .body {
      width: 100%;
      max-width: 1200px;
      display: flex;
      flex-direction: column;
      align-items: center;
      @include flex-gap($space-2, column);

      .html-content {
        font-size: $font-base;
        line-height: $line-height-base;
        color: $text-color;
        width: 100%;

        :deep(p) {
          margin: 0 0 $space-1 0;
          font-size: inherit;
          line-height: inherit;
          color: inherit;

          &:last-child {
            margin-bottom: 0;
          }
        }

        :deep(ul), :deep(ol) {
          margin: $list-spacing 0;
          padding-left: $list-indent;
        }

        :deep(li) {
          margin-bottom: $space-0-75;
        }

        :deep(strong) {
          font-weight: 700;
          color: $text-color;
        }

        :deep(em) {
          font-style: italic;
        }

        :deep(a) {
          color: $clr-tertiary;
          text-decoration: none;
        }

        &.html-content-truncated {
          @include line-clamp(4);
          transition: all 0.3s ease;
        }

        &.html-content-expanded {
          transition: all 0.3s ease;
        }
      }

      .expand-text-container {
        margin-top: clamp(1rem, 2vw, 1.5rem);
        display: flex;
        justify-content: center;
      }

      .expand-text-button {
        background: none;
        border: none;
        color: $clr-brand-dark;
        font-size: $font-list-meta;
        font-weight: 600;
        text-decoration: none;
        cursor: pointer;
        padding: clamp(0.75rem, 1.5vw, 1rem) clamp(1rem, 2vw, 1.5rem);
        border-radius: 4px;
        transition: all 0.3s ease;
        
        &:hover {
          color: $clr-tertiary;
          background-color: rgba($clr-tertiary, 0.1);
        }
        
        &:focus {
          outline: 2px solid $clr-tertiary;
          outline-offset: 2px;
        }
      }

      .pdf-container {
        width: 100%;
        max-width: 750px;
        margin-top: $space-2;
        
        :deep(.pdf-viewer-container) {
          aspect-ratio: 750 / 842; // Via di mezzo singola/doppia pagina A4
          height: auto;
          min-height: 600px;
          max-height: 600px;
          border-radius: 0;
        }
      }

      .image-container {
        width: 100%;
        max-width: 750px;
        min-height: 400px;
        max-height: 400px;
        margin-top: $space-1;
        display: flex;
        align-items: center;
        justify-content: center;
        
        .content-image {
          width: 100%;
          height: 100%;
          max-height: 400px;
          object-fit: contain;
          cursor: zoom-in;
        }
      }

      .carousel-container-wrapper {
        width: 100%;
        max-width: 750px;
        min-height: 400px;
        max-height: 400px;
        margin-top: $space-1;
        display: flex;
        align-items: center;
        justify-content: center;
        
        :deep(.carousel-image) {
          max-height: 400px;
          height: 100%;
          object-fit: contain;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   LIGHTBOX STYLES - Modal overlay per zoom
   ────────────────────────────────────────────────────────────── */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $space-2;
  cursor: zoom-out;
  animation: fadeIn 0.3s ease;

  .lightbox-content {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    align-items: center;
    justify-content: center;

    .lightbox-image {
      max-width: 100%;
      max-height: 90vh;
      object-fit: contain;
      cursor: default;
      animation: scaleIn 0.3s ease;
    }

    .lightbox-close {
      position: absolute;
      top: -40px;
      right: -40px;
      width: 40px;
      height: 40px;
      background: none;
      border: none;
      color: $clr-white;
      font-size: 3rem;
      cursor: pointer;
      transition: transform 0.2s ease;
      line-height: 1;
      padding: 0;

      &:hover {
        transform: scale(1.1);
      }
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Desktop grande (1300px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1300px) {
  .section-center-image-html {
    padding: $space-5 $container-padding-x-mob $space-4 $container-padding-x-mob;

    .content {
      @include flex-gap(clamp(1.5rem, 4vw, 2.5rem), column);

      .header {
        .eyebrow {
          font-size: $font-eyebrow-lg;
          
          &::before {
            height: 10px;
          }
        }

        .title {
          font-size: $font-title-lg;
        }
      }

      .body {
        max-width: 1000px;

        .html-content {
          font-size: $font-base-lg;
        }

        .pdf-container {
          max-width: 600px;
          
          :deep(.pdf-viewer-container) {
            aspect-ratio: 595 / 842;
            min-height: 700px;
          }
        }

        .image-container {
          max-width: 700px;
          min-height: 400px;
        }

        .carousel-container-wrapper {
          max-width: 700px;
          min-height: 400px;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Tablet (1024px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .section-center-image-html {
    .content {
      display: flex;
      flex-direction: column;
      @include flex-gap($space-1, column);

      .header {
        display: contents;

        .eyebrow {
          order: 1;
          margin-bottom: $space-0-5;
        }

        .title {
          order: 2;
          margin-bottom: 0;
        }
      }

      .body {
        display: contents;

        .pdf-container,
        .image-container,
        .carousel-container-wrapper {
          order: 3;
          max-width: 100%;
          margin-top: $space-1;
        }

        .html-content {
          order: 4;
          margin-top: $space-1;
        }

        .expand-text-container {
          order: 5;
        }

        .pdf-container {
          :deep(.pdf-viewer-container) {
            aspect-ratio: 595 / 842;
            min-height: 600px;
          }
        }

        .image-container,
        .carousel-container-wrapper {
          min-height: 400px;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Mobile (768px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .section-center-image-html {
    padding: $space-1 $space-1 $space-3 $space-1;

    .content {
      @include flex-gap($space-1, column);

      .header {
        .eyebrow {
          font-size: $font-eyebrow-sm;
          margin-bottom: $space-0-5;
        }

        .title {
          font-size: $font-title-sm;
        }
      }

      .body {
        @include flex-gap($space-1, column);

        .html-content {
          font-size: $font-base-sm;
        }

        .pdf-container,
        .image-container,
        .carousel-container-wrapper {
          margin-top: $space-0-5;
        }

        .pdf-container {
          :deep(.pdf-viewer-container) {
            aspect-ratio: 595 / 842;
            min-height: 500px;
          }
        }

        .image-container,
        .carousel-container-wrapper {
          min-height: 400px;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Small Mobile (480px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .section-center-image-html {
    padding: 0 $container-padding-x-mob $section-padding-y-mobile $container-padding-x-mob;

    .content {
      @include flex-gap($space-0-5, column);

      .header {
        align-items: flex-start;
        text-align: left;

        .eyebrow {
          /* .header e' display:contents su mobile: senza align-self l'elemento
             resta centrato dall'align-items:center di .content */
          align-self: stretch;
          font-size: $font-eyebrow-xs;
          margin-left: 20px;   /* bordo a 24px: 24 + 20 = 44, come chi-siamo */
          margin-bottom: $space-0-25;
          white-space: normal;
        }

        .title {
          align-self: stretch;
          font-size: $font-title-xs;
          margin-left: 20px;
          margin-bottom: $space-0-5;
        }
      }

      .body {
        @include flex-gap($space-0-5, column);

        .html-content {
          font-size: $font-base-xs;
        }

        .expand-text-container {
          margin-top: $space-0-25;
        }

        .pdf-container {
          margin-top: $space-0-5;
          :deep(.pdf-viewer-container) {
            aspect-ratio: 595 / 842;
            min-height: 550px;
          }
        }

        .image-container,
        .carousel-container-wrapper {
          min-height: auto;
          margin-top: $space-0-5;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Extra Small Mobile (360px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 360px) {
  .section-center-image-html {
    .content {
      .header {
        .eyebrow {
          font-size: $font-eyebrow-xxs;
          margin-left: 15px;   /* 24 + 15 = 39 */
        }

        .title {
          font-size: $font-title-xxs;
          margin-left: 15px;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   ALLINEAMENTO EYEBROW / TITOLO — layout collassato
   Sotto i 1024px .header diventa display:contents: eyebrow e titolo
   sono figli flex diretti di .content (align-items: center) e, avendo
   larghezze diverse, venivano centrati ognuno per conto suo — l'eyebrow
   finiva a destra rispetto al titolo. align-self li riporta sulla stessa
   x, il margin-left la allinea a quella di "chi siamo".
   Valori misurati sul riferimento: x=54@600, 102@768/850, 122@1024,
   44@390, 39@360.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .section-center-image-html .content .header {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 50px;
    }
  }

  .section-center-image-html .content .html-content {
    align-self: stretch;
    margin-left: 50px;
  }
}

@media (max-width: 920px) {
  .section-center-image-html .content .header {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 30px;
    }
  }

  .section-center-image-html .content .html-content {
    align-self: stretch;
    margin-left: 30px;
  }
}

@media (max-width: 768px) {
  .section-center-image-html .content .header {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 22px;
    }
  }

  .section-center-image-html .content .html-content {
    align-self: stretch;
    margin-left: 22px;
  }
}

@media (max-width: 480px) {
  .section-center-image-html .content .header {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 20px;
    }
  }

  .section-center-image-html .content .html-content {
    align-self: stretch;
    margin-left: 20px;
  }
}

@media (max-width: 360px) {
  .section-center-image-html .content .header {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 15px;
    }
  }

  .section-center-image-html .content .html-content {
    align-self: stretch;
    margin-left: 15px;
  }
}
</style>
